import { LogOut } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { ROLE_LABELS } from '@cfl/shared';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  useSidebar,
} from '@/components/cargofl-ui';
import { isActivePath, visibleNavSections, type NavBadges } from '@/lib/nav';
import { useCurrentUser, useSignOut } from '@/lib/session';

function initials(label: string): string {
  const parts = label.trim().split(/[\s@.]+/).filter(Boolean);
  return (parts[0]?.[0] ?? '?').concat(parts[1]?.[0] ?? '').toUpperCase();
}

/** How each icon moves, once, as its page becomes current (keyframes in styles/globals.css, `.nav-icon`); anything unlisted pops. */
const NAV_MOTION: Record<string, 'ring' | 'spin' | 'drop' | 'pop'> = {
  '/team/meetings': 'ring',
  '/dev-connect': 'ring',
  '/hr/cycles': 'spin',
  '/admin/organization': 'spin',
};

export function AppSidebar({ badges }: { badges?: NavBadges }) {
  const { pathname } = useLocation();
  const user = useCurrentUser();
  const signOut = useSignOut();
  // `expanded`, not `open` — between md and lg the sidebar is a rail even when "open".
  const { expanded, setOpenMobile } = useSidebar();

  const sections = visibleNavSections({ role: user.role, hasReports: user.hasReports });

  return (
    <TooltipProvider delayDuration={0}>
      <Sidebar>
        {/* Fixed height matching the top bar so the logo sits on the same baseline
            as the page title across the divider. */}
        <SidebarHeader className="h-[60px] justify-center border-b border-sidebar-border">
          <Link
            to="/dashboard"
            className="flex items-center px-2 group-data-[state=closed]/sidebar:justify-center group-data-[state=closed]/sidebar:px-0"
          >
            {/* The mark is dark navy; brightness-0 invert renders it white on the
                dark teal sidebar panel. */}
            <img
              src={expanded ? '/logo-wordmark.svg' : '/favicon.png'}
              alt="CargoFL"
              draggable={false}
              className={
                expanded
                  ? 'h-[22px] w-auto max-w-[130px] object-contain object-left brightness-0 invert'
                  : 'size-6 rounded'
              }
            />
          </Link>
        </SidebarHeader>

        <SidebarContent>
          {sections.map((section) => (
            <SidebarGroup key={section.label}>
              {/* The rail has no room for a group label, and an 11px truncated word
                  reads as noise rather than structure. */}
              {expanded && <SidebarGroupLabel>{section.label}</SidebarGroupLabel>}
              <SidebarGroupContent>
                <SidebarMenu>
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const badge = item.badgeKey ? badges?.[item.badgeKey] : undefined;
                    const showBadge = !!badge && badge.count > 0;
                    const button = (
                      <SidebarMenuButton asChild isActive={isActivePath(pathname, item.href)}>
                        {/* Tapping a destination closes the mobile drawer; no-op on desktop. */}
                        <Link to={item.href} onClick={() => setOpenMobile(false)} aria-current={isActivePath(pathname, item.href) ? 'page' : undefined}>
                          <Icon className="nav-icon" data-motion={NAV_MOTION[item.href] ?? 'pop'} />
                          <span>{item.title}</span>
                          {showBadge && (
                            <Badge
                              variant={badge.urgent ? 'destructive' : 'secondary'}
                              className="ml-auto rounded-full px-2 tabular-nums"
                            >
                              {badge.count}
                            </Badge>
                          )}
                        </Link>
                      </SidebarMenuButton>
                    );

                    return (
                      <SidebarMenuItem key={item.href}>
                        {expanded ? (
                          button
                        ) : (
                          // Collapsed rail: the label lives in a hover tooltip.
                          <Tooltip delayDuration={0}>
                            <TooltipTrigger asChild>{button}</TooltipTrigger>
                            <TooltipContent side="right">
                              {item.title}
                              {showBadge ? ` · ${badge.count}` : ''}
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ))}
        </SidebarContent>

        <SidebarFooter>
          <div className="flex items-center gap-2.5 px-2 py-1.5 group-data-[state=closed]/sidebar:justify-center group-data-[state=closed]/sidebar:px-0">
            <Avatar className="size-8 shrink-0">
              {user.avatarUrl ? <AvatarImage src={user.avatarUrl} alt={user.name} /> : null}
              <AvatarFallback className="bg-[hsl(174_62%_33%)] text-xs font-semibold text-white">
                {initials(user.name)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1 leading-tight group-data-[state=closed]/sidebar:hidden">
              <div className="truncate text-[13px] font-semibold text-sidebar-foreground">
                {user.name}
              </div>
              {/* Job title if we have one, else the permission role. Showing "HR" to
                  a recruiter would be accurate but not how they think of themselves. */}
              <div className="truncate text-[11px] text-sidebar-foreground/60">
                {user.designationTitle ?? ROLE_LABELS[user.role]}
              </div>
            </div>
            <button
              onClick={() => void signOut()}
              title="Log out"
              className="rounded p-1 text-sidebar-foreground/60 transition-colors hover:text-sidebar-foreground group-data-[state=closed]/sidebar:hidden"
            >
              <LogOut className="size-4" />
            </button>
          </div>
        </SidebarFooter>
      </Sidebar>
    </TooltipProvider>
  );
}
